import React, { useState, useEffect } from 'react';
import { useConfirm } from '../context/ConfirmContext';
import { Person, ProjectPerson, CreateProjectPerson } from '../types';
import { personsAPI, projectPersonsAPI } from '../services/api';

interface PendingProjectPerson {
  tempId: number;
  personId: number;
  personFirstName: string;
  personLastName: string;
  role?: string;
}

interface ProjectPersonsProps {
  projectId?: number;
  isEditing: boolean;
  onPendingPersonsChange?: (persons: CreateProjectPerson[]) => void;
}

const ProjectPersons: React.FC<ProjectPersonsProps> = ({ projectId, isEditing, onPendingPersonsChange }) => {
  const [persons, setPersons] = useState<Person[]>([]);
  const [projectPersons, setProjectPersons] = useState<ProjectPerson[]>([]);
  const [pendingPersons, setPendingPersons] = useState<PendingProjectPerson[]>([]);
  const [selectedPersonId, setSelectedPersonId] = useState<number>(0);
  const [role, setRole] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [tempIdCounter, setTempIdCounter] = useState(1);
  const confirm = useConfirm();

  useEffect(() => {
    loadPersons();
    if (projectId) {
      loadProjectPersons();
    }
  }, [projectId]);

  // Notify parent of pending persons changes
  useEffect(() => {
    if (!projectId && onPendingPersonsChange) {
      const createPersonsData = pendingPersons.map(pp => ({
        projectId: 0, // Will be set when project is created
        personId: pp.personId,
        role: pp.role
      }));
      onPendingPersonsChange(createPersonsData);
    }
  }, [pendingPersons, projectId, onPendingPersonsChange]);

  const loadPersons = async () => {
    try {
      const data = await personsAPI.getPersons();
      setPersons(data);
    } catch (error) {
      console.error('Error loading persons:', error);
    }
  };

  const loadProjectPersons = async () => {
    if (!projectId) return;

    try {
      setLoading(true);
      const data = await projectPersonsAPI.getProjectPersonsByProject(projectId);
      setProjectPersons(data);
    } catch (error) {
      console.error('Error loading project persons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!selectedPersonId) return;

    // If no projectId, add to pending list
    if (!projectId) {
      const selectedPerson = persons.find(p => p.id === selectedPersonId);
      if (!selectedPerson) return;

      const newPending: PendingProjectPerson = {
        tempId: tempIdCounter,
        personId: selectedPersonId,
        personFirstName: selectedPerson.firstName,
        personLastName: selectedPerson.lastName,
        role: role || undefined
      };

      setPendingPersons([...pendingPersons, newPending]);
      setTempIdCounter(tempIdCounter + 1);
      setSelectedPersonId(0);
      setRole('');
      return;
    }

    // If projectId exists, save to API
    try {
      setLoading(true);
      const newProjectPerson: CreateProjectPerson = {
        projectId,
        personId: selectedPersonId,
        role: role || undefined
      };

      await projectPersonsAPI.createProjectPerson(newProjectPerson);
      await loadProjectPersons();
      setSelectedPersonId(0);
      setRole('');
    } catch (error) {
      console.error('Error adding project person:', error);
      alert('Failed to add person to project');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (id: number) => {
    const projectPerson = projectPersons.find(pp => pp.id === id);
    if (!projectPerson) return;

    try {
      setLoading(true);
      await projectPersonsAPI.updateProjectPerson(id, {
        id,
        projectId: projectPerson.projectId,
        personId: projectPerson.personId,
        role: role || undefined
      });
      await loadProjectPersons();
      setEditingId(null);
      setRole('');
    } catch (error) {
      console.error('Error updating project person:', error);
      alert('Failed to update person role');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    const isConfirmed = await confirm({
      title: 'Remove assigned person',
      message: 'Are you sure you want to remove this person from the project?',
      confirmText: 'Remove',
      cancelText: 'Cancel',
      variant: 'danger'
    });
    if (!isConfirmed) return;

    try {
      setLoading(true);
      await projectPersonsAPI.deleteProjectPerson(id);
      await loadProjectPersons();
    } catch (error) {
      console.error('Error deleting project person:', error);
      alert('Failed to remove person from project');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePending = (tempId: number) => {
    const updatedPending = pendingPersons.map(pp =>
      pp.tempId === tempId ? { ...pp, role: role || undefined } : pp
    );
    setPendingPersons(updatedPending);
    setEditingId(null);
    setRole('');
  };

  const handleDeletePending = (tempId: number) => {
    setPendingPersons(pendingPersons.filter(pp => pp.tempId !== tempId));
  };

  const startEdit = (projectPerson: ProjectPerson) => {
    setEditingId(projectPerson.id);
    setRole(projectPerson.role || '');
  };

  const startEditPending = (pending: PendingProjectPerson) => {
    setEditingId(pending.tempId);
    setRole(pending.role || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setRole('');
  };

  if (!isEditing) {
    return null;
  }

  const assignedPersonIds = projectId
    ? projectPersons.map(pp => pp.personId)
    : pendingPersons.map(pp => pp.personId);

  const displayPersons = projectId ? projectPersons : pendingPersons;

  return (
    <div className="card">
      <div className="card-header">
        <h6 className="mb-0">Assigned Persons</h6>
      </div>
      <div className="card-body">
        {/* Add new person */}
        <div className="row g-3 mb-3">
          <div className="col-md-5">
            <label htmlFor="personSelect" className="form-label">Person</label>
            <select
              id="personSelect"
              className="form-select"
              value={selectedPersonId}
              onChange={(e) => setSelectedPersonId(parseInt(e.target.value))}
              disabled={loading}
            >
              <option value="0">Select person...</option>
              {persons
                .filter(p => !assignedPersonIds.includes(p.id))
                .map(person => (
                  <option key={person.id} value={person.id}>
                    {person.firstName} {person.lastName}
                  </option>
                ))}
            </select>
          </div>
          <div className="col-md-5">
            <label htmlFor="roleInput" className="form-label">Role</label>
            <input
              type="text"
              id="roleInput"
              className="form-control"
              placeholder="e.g., Project Manager, Developer..."
              value={role}
              onChange={(e) => setRole(e.target.value)}
              disabled={loading}
              maxLength={256}
            />
          </div>
          <div className="col-md-2 d-flex align-items-end justify-content-end">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAdd}
              disabled={loading || selectedPersonId === 0}
            >
              Add
            </button>
          </div>
        </div>

        {/* List of assigned persons */}
        {displayPersons.length === 0 ? (
          <div className="text-center text-muted py-3">
            No persons assigned to this project yet.
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-light">
                <tr>
                  <th className="text-start">Name</th>
                  <th className="text-start">Role</th>
                  <th className="text-start" style={{ width: '150px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {projectId ? (
                  // Render saved project persons
                  projectPersons.map(pp => (
                  <tr key={pp.id}>
                    <td>{pp.personFirstName} {pp.personLastName}</td>
                    <td>
                      {editingId === pp.id ? (
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={role}
                          onChange={(e) => setRole(e.target.value)}
                          maxLength={256}
                        />
                      ) : (
                        pp.role || <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {editingId === pp.id ? (
                        <div className="btn-group action-group" role="group">
                          <button
                            type="button"
                            className="btn action-btn action-edit"
                            onClick={() => handleUpdate(pp.id)}
                            disabled={loading}
                            title="Save"
                          >
                            <i className="fa-solid fa-check" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="btn action-btn action-delete"
                            onClick={cancelEdit}
                            disabled={loading}
                            title="Cancel"
                          >
                            <i className="fa-solid fa-xmark" aria-hidden="true" />
                          </button>
                        </div>
                      ) : (
                        <div className="btn-group action-group" role="group">
                          <button
                            type="button"
                            className="btn action-btn action-edit"
                            onClick={() => startEdit(pp)}
                            disabled={loading}
                            title="Edit role"
                          >
                            <i className="fa-solid fa-pen" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="btn action-btn action-delete"
                            onClick={() => handleDelete(pp.id)}
                            disabled={loading}
                            title="Remove person"
                          >
                            <i className="fa-solid fa-trash" aria-hidden="true" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                  ))
                ) : (
                  // Render pending project persons (before project is saved)
                  pendingPersons.map(pp => (
                  <tr key={pp.tempId}>
                    <td>{pp.personFirstName} {pp.personLastName}</td>
                    <td>
                      {editingId === pp.tempId ? (
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={role}
                          onChange={(e) => setRole(e.target.value)}
                          maxLength={256}
                        />
                      ) : (
                        pp.role || <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {editingId === pp.tempId ? (
                        <div className="btn-group action-group" role="group">
                          <button
                            type="button"
                            className="btn action-btn action-edit"
                            onClick={() => handleUpdatePending(pp.tempId)}
                            title="Save"
                          >
                            <i className="fa-solid fa-check" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="btn action-btn action-delete"
                            onClick={cancelEdit}
                            title="Cancel"
                          >
                            <i className="fa-solid fa-xmark" aria-hidden="true" />
                          </button>
                        </div>
                      ) : (
                        <div className="btn-group action-group" role="group">
                          <button
                            type="button"
                            className="btn action-btn action-edit"
                            onClick={() => startEditPending(pp)}
                            title="Edit role"
                          >
                            <i className="fa-solid fa-pen" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="btn action-btn action-delete"
                            onClick={() => handleDeletePending(pp.tempId)}
                            title="Remove person"
                          >
                            <i className="fa-solid fa-trash" aria-hidden="true" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectPersons;
